// $FlowFixMe
import {
  useEffect,
  useState,
  useRef,
  ChangeEvent,
  useCallback,
  useMemo,
} from 'react';
import { isEmpty, sort, comparator, gt, assocPath, pick, equals } from 'ramda';
import { toastr } from 'react-redux-toastr';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import _ from 'lodash';
import moment from 'moment';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { ASSET_PROTECT } from './IntegrationsSetup/constants';
import PropertyService from '../../services/propertyService';
import MarketingSectionService from '../../services/marketingSectionService';
import IntegrationService from '../../services/integrationService';
import messages from './messages';
import { MasterLeaseTermsService } from '../../services/masterLeaseTermsService';
import ProrateMethodsService from '../../services/prorateMethodsService';
import {
  MARKETING_INFO_UPDATE_ERROR,
  PROPERTY_UPDATE_ERROR,
  INTEGRATION_UPDATE_ERROR,
  CDLAC_NUMBER_VALIDATION_REGEX,
} from './constants';
import {
  validateEmail,
  validatePhoneNumber,
  validateZipCode,
  validateMaxLength,
} from '../../utils/validations';
import RoleService from '../../services/roleService';
import { isOpenDateMissing } from './AffordableSetup/WaitlistV2/utils';
import {
  COLLECTIONS_DEFAULT_DAYS_AFTER_FAS,
  COLLECTIONS_DEFAULT_MINIMUM_AMOUNT,
} from '../../constants/propertyCollectionSettings';
import { cleanData, numberInRange, getScreeningSetupData } from './utils';
import {
  HUD_MINIMUM_RENT_MAX,
  HUD_MINIMUM_RENT_MIN,
} from './AffordableSetup/Programs/HUD/constants';
import {
  hasEditedHUDSetupIdentifierError,
  hasHUDContractIdentifierErrors,
} from './AffordableSetup/Programs/HUD/identifierValidation';
import { ELIGIBILITY_UNIVERSE_CODE_ENUM } from '../../constants/hudEligibilityUniverseCodes';

export const useAsyncPropertyDetails = (
  organizationId: string,
  propertyId: string,
  intl: any,
  selectProperty: Function,
  updateCurrentUserSelectedProperty: Function,
) => {
  const isMounted = useRef(true);
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  const [propertyInfo, setPropertyInfo] = useState({
    leaseRentPercentage: '',
    propertyLateMethods: [],
    propertyLeaseTerms: [],
    propertyIntegrations: [],
    hubPremiumFeatures: [],
    isSetLeaseEndDateToEndOfMonth: false,
    leaseEndDateGreaterThan12m: 'CALCULATED',
    leaseEndDateLesserThan12m: 'CALCULATED',
    propertyCollectionSetting: {
      enabled: false,
      minimumAmount: COLLECTIONS_DEFAULT_MINIMUM_AMOUNT,
      daysAfterFAS: COLLECTIONS_DEFAULT_DAYS_AFTER_FAS,
    },
    config: {
      applicationAutoScreening: false,
      successiveLeaseTerm: 'monthly',
      autoSendToTRACS: true,
      addUtilityNameToUtilityReimbursementChecks: false,
      recertTextReminders: false,
      automatedProspectText: false,
      isApplicationsOpen: false,
      masterMeteredUtilities: {},
      accrueSecurityDepositInterest: false,
      securityDepositInterestPayoutFrequency: null,
      agedUpMinorType: null,
      sprinklerInspectionDate: null,
    },
    mobileAppSetup: { residentCommunications: false },
    paymentProviders: [],
    loanTransferAmount: null,
    noteRentPayment: null,
    icPayment: null,
    stateConfigs: [],
  });

  const handleNullValues = (propertyInfo: Object) => {
    const [propertyChangeHistoryLog] = sort(
      comparator((a, b) => gt(a.changeDate, b.changeDate)),
      propertyInfo.propertyChangeHistoryLog,
    );
    const affordableProps = pick(['setup', 'pap'], propertyInfo);

    return {
      name: propertyInfo.name,
      shortName: propertyInfo.shortName,
      id: propertyInfo.id,
      onboardingQueue: propertyInfo.onboardingQueue,
      portalLatestOnboardingGroupId: propertyInfo.portalLatestOnboardingGroupId,
      portalLatestOnboardingById: propertyInfo.portalLatestOnboardingById,
      portalUpdatedAt: propertyInfo.portalUpdatedAt,
      phoneNumber: propertyInfo?.phoneNumber ?? '',
      fax: propertyInfo?.faxNumber ?? '',
      email: propertyInfo?.propertyEmail ?? '',
      address1: propertyInfo?.physicalStreetAddress1 ?? '',
      address2: propertyInfo?.physicalStreetAddress2 ?? '',
      city: propertyInfo?.physicalCity ?? '',
      state: propertyInfo?.physicalState ?? '',
      zip: propertyInfo?.physicalZip ?? '',
      owner: propertyInfo?.propertyOwner?.name ?? '---',
      propertyClass: propertyInfo?.propertyClass?.name ?? '---',
      status: propertyInfo?.propertyStatus?.description ?? '---',
      isResidentPortalActive: propertyInfo?.isResidentPortalActive ?? false,
      isElectronicLeaseSigningActive:
        propertyInfo?.isElectronicLeaseSigningActive ?? false,
      legalEntity: propertyInfo?.ownershipEntity?.name ?? '---',
      pfp: propertyInfo?.pfp?.periodName ?? '---',
      isDomusoActive: propertyInfo.isDomusoActive,
      isPayLeaseActive: propertyInfo.isPayLeaseActive,
      isTransUnionActive: propertyInfo.isTransUnionActive,
      domusoPropertyCode: propertyInfo?.domusoPropertyCode ?? '',
      leaseRentPercentage: propertyInfo?.leaseRentPercentage ?? '20',
      sitePetWeightMax: propertyInfo?.sitePetWeightMax ?? '---',
      turnMoveOutDays: propertyInfo?.turnMoveOutDays ?? '---',
      propertyLateMethods: propertyInfo?.propertyLateMethods ?? [],
      propertyLeaseTerms: propertyInfo?.propertyLeaseTerms ?? [],
      hasCommercialFloorPlans: propertyInfo?.hasCommercialFloorPlans ?? 'NONE',
      propertyBankAccounts: propertyInfo?.propertyBankAccounts ?? [],
      gpr:
        propertyInfo?.propertyReportConfiguration?.['0']?.config1Value ?? '---',
      propertyChangeHistoryLog,
      cashPrepaidGLCode: propertyInfo?.cashPrepaidGLCode ?? [],
      applicationIncomeMultiplier:
        propertyInfo?.applicationIncomeMultiplier ?? '',
      renewalProrateMethodId: propertyInfo?.renewalProrateMethodId ?? null,
      propertyIntegrations: propertyInfo?.integrations ?? [],
      isLeaseExpirationLimitsActive:
        propertyInfo?.isLeaseExpirationLimitsActive,
      ...affordableProps,
      moveOutProrateMethodId: propertyInfo?.moveOutProrateMethodId ?? null,
      deprecated_moveOutProrateMethodId:
        propertyInfo?.deprecated_moveOutProrateMethodId ?? null,
      isSetLeaseEndDateToEndOfMonth:
        propertyInfo?.isSetLeaseEndDateToEndOfMonth ?? false,
      leaseEndDateGreaterThan12m:
        propertyInfo?.leaseEndDateGreaterThan12m ?? 'CALCULATED',
      leaseEndDateLesserThan12m:
        propertyInfo?.leaseEndDateLesserThan12m ?? 'CALCULATED',
      quoteExpTime: propertyInfo?.quoteExpTime ?? null,
      isNoEndDateActive: propertyInfo?.isNoEndDateActive,
      insuranceType: propertyInfo.insuranceType ?? 'None',
      revenueManagementProviderName:
        propertyInfo?.revenueManagementProviderName ?? null,
      insuranceFeeAmount: propertyInfo.insuranceFeeAmount,
      insuranceMasterTransactionCodeId:
        propertyInfo.config?.insuranceMasterTransactionCodeId ??
        propertyInfo.insuranceMasterTransactionCodeId ??
        'default',
      propertyRiskManagement: propertyInfo.propertyRiskManagement ?? [],
      ownershipEntity: {
        ...propertyInfo.ownershipEntity,
      },
      organizationCollectionAgencies:
        propertyInfo?.organizationCollectionAgencies,
      propertyCollectionSetting: propertyInfo.propertyCollectionSetting
        ? pick(
            ['enabled', 'minimumAmount', 'daysAfterFAS', 'collectionAgency'],
            propertyInfo.propertyCollectionSetting,
          )
        : {
            enabled: false,
            minimumAmount: COLLECTIONS_DEFAULT_MINIMUM_AMOUNT,
            daysAfterFAS: COLLECTIONS_DEFAULT_DAYS_AFTER_FAS,
          },
      config: propertyInfo.config
        ? pick(
            [
              'adverseActionDeadlineDays',
              'applicationAutoScreening',
              'autoPostMonthlyCharges',
              'residentCommunicationEnabled',
              'autoChargeMtmFees',
              'roundProratedRents',
              'successiveLeaseTerm',
              'autoSendToTRACS',
              'housingAssistanceVouchers',
              'unitAvailabilityVersion',
              'addUtilityNameToUtilityReimbursementChecks',
              'isApplicationsOpen',
              'accountingIntegration',
              'recertTextReminders',
              'automatedProspectText',
              'masterMeteredUtilities',
              'accrueSecurityDepositInterest',
              'securityDepositInterestPayoutFrequency',
              'landParcelNumber',
              'agedUpMinorType',
              'mtmRentSource',
              'shortTermLeaseThreshold',
              'allowThirdPartyInsurancePolicies',
              'sprinklerInspectionDate',
              'timezone',
            ],
            propertyInfo.config,
          )
        : {},
      hubPremiumFeatures: propertyInfo.hubPremiumFeatures ?? [],
      propertyType: propertyInfo.propertyClass?.propertyType ?? 'Conventional',
      isTwoWayCommunicationActive:
        propertyInfo.isTwoWayCommunicationActive ?? false,
      textingPhoneNumber:
        propertyInfo.phoneNumbers?.find((p) => p.campaign === 'Customer Care')
          ?.phoneNumber ?? '',
      paymentProviders: propertyInfo.paymentProviders ?? [],
      electricCompany: propertyInfo.electricCompany ?? '',
      secondaryPropertyTypeId: propertyInfo.secondaryPropertyTypeId,
      developmentPhase: propertyInfo.developmentPhase ?? null,

      loanTransferAmount: propertyInfo.loanTransferAmount ?? null,
      noteRentPayment: propertyInfo.noteRentPayment ?? null,
      icPayment: propertyInfo.icPayment ?? null,

      screeningSetupData: getScreeningSetupData({
        providerPropertyProfiles: propertyInfo?.providerPropertyProfiles ?? [],
        screeningPropertyConfigs: propertyInfo?.screeningPropertyConfigs ?? [],
        allProviders: propertyInfo?.allProviders ?? [],
      }),
      stateConfigs: propertyInfo.stateConfigs ?? [],
    };
  };

  /**
   * Updates the property settings to be used during the user session
   * ---
   * @relatedActions
   *  - actions.updateSelectedProperty()
   * @param {*} data
   * @param {*} intl
   * @param {*} throwError
   */
  const updateProperty = async (
    data: Object,
    intl: Object,
    throwError?: boolean,
    notificationAttributes?: Object,
  ) => {
    const propertyService = new PropertyService();

    try {
      const response = await propertyService.update(
        organizationId,
        propertyId,
        data,
        notificationAttributes,
      );

      if (response && isMounted.current) {
        setPropertyInfo(handleNullValues(response));
        selectProperty(response);
        updateCurrentUserSelectedProperty(response);
      }
    } catch (e) {
      if (isMounted.current) {
        setPropertyInfo({ ...propertyInfo });
      }
      if (throwError) {
        throw PROPERTY_UPDATE_ERROR;
      }
    }
  };

  const updatePropertyIntegrations = async (
    integrationsChanges: Object,
    intl: Object,
    throwError?: boolean,
  ) => {
    const integrationService = new IntegrationService();
    if (!integrationsChanges) {
      return;
    }
    // filter out null/undefined values
    const previousIntegrations = propertyInfo.propertyIntegrations;
    const integrations = Object.values(integrationsChanges).filter((x) => x);
    const integrationUpdateRequests = integrations.map((inte) => {
      return integrationService.updateIntegrationProperty(
        organizationId,
        propertyId,
        inte.integrationId,
        { isActive: inte.value },
      );
    });
    try {
      const updatedIntegrations = await Promise.all(integrationUpdateRequests);
      return updatedIntegrations;
    } catch (error) {
      if (isMounted.current) {
        setPropertyInfo({
          ...propertyInfo,
          propertyIntegrations: previousIntegrations,
        });
      }
      if (throwError) {
        throw INTEGRATION_UPDATE_ERROR;
      }
    }
  };

  useEffect(() => {
    // $FlowFixMe
    const abortController = new AbortController(); // eslint-disable-line
    const options = { signal: abortController.signal };
    let subscribed = true;

    const fetchPropertyDetails = async () => {
      try {
        const propertyService = new PropertyService();
        setIsLoading(true);
        const response = await propertyService.getPropertyById(
          organizationId,
          propertyId,
          options,
        );
        if (response && subscribed) {
          setPropertyInfo(handleNullValues(response));
          setIsLoading(false);
        }
      } catch (error) {
        toastr.error(
          intl.formatMessage(messages.error),
          intl.formatMessage(messages.fetchPropertyErrorBody),
        );
      }
    };
    if (subscribed && !isEmpty(organizationId)) {
      fetchPropertyDetails();
    }
    return () => {
      subscribed = false;
    };
  }, [organizationId, propertyId, setPropertyInfo, intl]);

  return [propertyInfo, updateProperty, updatePropertyIntegrations, isLoading];
};

export const useForm = (initialValues: Object) => {
  const [values, setValues] = useState(initialValues);

  const onChange = useCallback(({ target: { name, value } }: ChangeEvent) => {
    const path = typeof name === 'string' ? name.split('.') : [];
    setValues((prevState) => assocPath(path, value, prevState));
  }, []);

  return [values, setValues, onChange];
};

export const useAsyncMarketingSection = (
  organizationId: string,
  propertyId: string,
) => {
  const isMounted = useRef(true);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const [marketingInfo, setMarketingInfo] = useState({
    name: '',
    propertyLogoLink: '',
    pitch1: '',
    officeHoursDescription: '',
    propertyDescription: '',
    petPolicyDescription: '',
    parkingDetailDescription: '',
    amenities: [],
  });

  const handleNullValues = (marketingInfo: Object) => ({
    name: marketingInfo.name,
    propertyLogoLink: marketingInfo?.propertyLogoLink ?? '',
    pitch1: marketingInfo?.pitch1 ?? '',
    officeHoursDescription: marketingInfo?.officeHoursDescription ?? '',
    propertyDescription: marketingInfo?.propertyDescription ?? '',
    petPolicyDescription: marketingInfo?.petPolicyDescription ?? '',
    parkingDetailDescription: marketingInfo?.parkingDetailDescription ?? '',
    amenities:
      marketingInfo?.propertyAmenities?.map((amenity) => amenity.name) ?? [],
  });

  const updateMarketingSection = async (
    data: Object,
    intl: Object,
    throwError?: boolean,
  ) => {
    const marketingSectionService = new MarketingSectionService();

    try {
      const response = await marketingSectionService.update(
        organizationId,
        propertyId,
        data,
      );

      if (response && isMounted.current) {
        setMarketingInfo(handleNullValues(response));
      }
    } catch (err) {
      if (isMounted.current) {
        setMarketingInfo({ ...marketingInfo });
      }
      if (throwError) {
        throw MARKETING_INFO_UPDATE_ERROR;
      }
    }
  };

  useEffect(() => {
    // $FlowFixMe
    const abortController = new AbortController(); // eslint-disable-line
    const options = { signal: abortController.signal };
    let subscribed = true;

    const fetchMarketingDetails = async () => {
      const marketingSectionService = new MarketingSectionService();
      setIsLoading(true);
      const response = await marketingSectionService.getProperty(
        organizationId,
        propertyId,
        options,
      );
      if (response && subscribed) {
        setMarketingInfo(handleNullValues(response));
        setIsLoading(false);
      }
    };

    if (subscribed && !isEmpty(organizationId)) {
      fetchMarketingDetails();
    }
    return () => {
      subscribed = false;
    };
  }, [organizationId, propertyId, setMarketingInfo]);

  return [marketingInfo, updateMarketingSection, isLoading];
};

export const useAsyncMasterLeaseTerms = (organizationId: string) => {
  const isMounted = useRef(true);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const [masterLeaseTerms, setMasterLeaseTerms] = useState([]);

  useEffect(() => {
    (async () => {
      const masterLeaseTermsService = new MasterLeaseTermsService();
      const response = await masterLeaseTermsService.getAllMasterLeaseTerms(
        organizationId,
      );

      if (isMounted.current && response) {
        setMasterLeaseTerms(response);
      }
    })();
  }, [organizationId]);

  return [masterLeaseTerms];
};

export const useAsyncProrateMethods = (): any => {
  const isMounted = useRef(true);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const [prorateMethods, setProrateMethods] = useState([]);

  useEffect(() => {
    (async () => {
      const prorateMethodsService = new ProrateMethodsService();
      const response = await prorateMethodsService.getAll();
      if (isMounted.current && response) {
        setProrateMethods(response);
      }
    })();
  }, []);

  return [prorateMethods];
};

export const useAsyncValidateFirstOfMonthPostEnabled = ({
  propertyId,
  organizationId,
}: Object): any => {
  const [enabledStatusMLT, setEnabledStatusMLT] = useState(null);
  const [errorStatusMLT, setErrorStatusMLT] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldRefresh, setShouldRefresh] = useState(true);

  useEffect(() => {
    // $FlowFixMe
    const abortController = new AbortController(); // eslint-disable-line

    const fetch = async () => {
      if (organizationId && propertyId) {
        setIsLoading(true);
        const propertyService = new PropertyService();
        const response = await propertyService.checkEnableFirstOfMonthPosting(
          organizationId,
          propertyId,
          abortController.signal,
        );
        setEnabledStatusMLT(response.enable);
        setErrorStatusMLT(response.error);
        setIsLoading(false);
      }
    };

    fetch();
    setShouldRefresh(false);

    return () => abortController.abort();
  }, [organizationId, propertyId, shouldRefresh]);

  return [isLoading, enabledStatusMLT, errorStatusMLT];
};

export function usePostFirstOfMonthMLT(
  intl: any,
  propertyId: string,
  organizationId: string,
  toasterFn: Function,
) {
  const [isPosting, setIsPosting] = useState(false);

  const postMLT = async () => {
    if (isPosting) return;

    const service = new PropertyService();

    setIsPosting(true);

    try {
      const response = await service.postFirstOfMonthMLT(
        organizationId,
        propertyId,
      );
      if (response.error) throw new Error(response.error);
      toasterFn({
        type: 'info',
        title: intl.formatMessage(messages.inProgress),
        message: intl.formatMessage(messages.postMLTInProgress),
      });
    } catch (e) {
      toasterFn({
        type: 'error',
        message: e.toString(),
        title: intl.formatMessage(messages.error),
      });
    }

    setIsPosting(false);
  };

  return [postMLT, isPosting];
}
export function usePropertyRoles({ organizationId, propertyId }) {
  const { propertyRoles, isLoading, roleAssignments, setRoleAssignments } =
    useFetchPropertyRoleAssignments(organizationId, propertyId);

  const handleAssignmentChange = (propertyRoleId, userId) => {
    const updatedRoleAssignments = roleAssignments.map((roleAssignment) => {
      if (roleAssignment.id === propertyRoleId) {
        return {
          ...roleAssignment,
          roleAssignment: {
            id: userId,
            name: propertyRoles
              .find((role) => role.id === propertyRoleId)
              ?.assignmentOptions?.find((option) => {
                return option.id === userId;
              })?.name,
          },
        };
      }
      return roleAssignment;
    });
    setRoleAssignments(updatedRoleAssignments);
  };
  const assignPropertyRoles = useAssignPropertyRoles({
    propertyId,
    roleAssignments: roleAssignments.filter((ra) => {
      return !!ra.roleAssignment.id && ra.roleAssignment.id !== '';
    }),
  });

  return {
    propertyRoles,
    isLoading,
    roleAssignments,
    setRoleAssignments,
    handleAssignmentChange,
    assignPropertyRoles,
  };
}

const useFetchPropertyRoleAssignments = (organizationId, propertyId) => {
  const getName = (user) => {
    if (!user?.firstName || !user?.lastName) return null;
    return `${user?.firstName} ${user?.lastName}`;
  };
  const roleService = new RoleService();
  const queryKey = ['propertyRoleAssignments', organizationId, propertyId];
  const options = {
    initialData: [],
  };
  const { data, isLoading } = useQuery(
    queryKey,
    () => roleService.getPropertyRoleAssignments(organizationId, propertyId),
    options,
  );
  const propertyRoles = useMemo(() => {
    return data.map((datum) => {
      const assignmentOptions = [];
      datum.userRoles.forEach((userRole) => {
        assignmentOptions.push(
          userRole.assignedUsers.map((user) => {
            return {
              id: user.id,
              name: `${user.firstName} ${user.lastName}`,
            };
          }),
        );
      });

      return {
        ..._.omit(datum, ['userRoles', 'propertyRoleAssignments']),
        assignmentOptions: assignmentOptions
          .flat()
          .sort((a, b) => a.name.localeCompare(b.name)),
        currentAssignedUser: {
          id: datum.propertyRoleAssignments?.[0]?.assignedUser.id,
          name: getName(datum.propertyRoleAssignments?.[0]?.assignedUser),
        },
      };
    });
  }, [data]);

  const initialRoleAssignments = useMemo(() => {
    return propertyRoles.map((propertyRole) => {
      return {
        id: propertyRole?.id,
        name: propertyRole?.name,
        roleAssignment: {
          id: propertyRole?.currentAssignedUser?.id,
          name: propertyRole?.currentAssignedUser?.name,
        },
      };
    });
  }, [propertyRoles]);

  const [roleAssignments, setRoleAssignments] = useState([]);

  useEffect(() => {
    setRoleAssignments(initialRoleAssignments);
  }, [initialRoleAssignments]);

  return {
    propertyRoles,
    isLoading,
    roleAssignments,
    setRoleAssignments,
  };
};

export const useAssignPropertyRoles = ({ propertyId, roleAssignments }) => {
  const queryClient = useQueryClient();
  const roleService = new RoleService();
  const assignPropertyRoles = async () => {
    return roleService.assignPropertyRoles(propertyId, roleAssignments);
  };

  const assignPropertyRolesMutation = useMutation(assignPropertyRoles, {
    onSuccess: () => {
      queryClient.invalidateQueries('propertyRoleAssignments');
    },
  });
  return assignPropertyRolesMutation.mutate;
};

export const useMoveOutProrateMethods = () => {
  const queryKey = 'moveOutProrateMethods';
  const options = {
    initialData: [],
  };
  const { data } = useQuery(
    queryKey,
    () => {
      const prorateMethodsService = new ProrateMethodsService();
      return prorateMethodsService.getMoveOutProrateMethods();
    },
    options,
  );

  return data;
};

export const useOrgSecondaryPropertyTypes = (
  organizationId,
  propertySecondaryTypeId,
) => {
  const secondaryPropertyTypes =
    useFetchOrgSecondaryPropertyTypes(organizationId);
  const [selectedPropertySecondaryType, setSelectedPropertySecondaryType] =
    useState(null);

  useEffect(() => {
    setSelectedPropertySecondaryType(propertySecondaryTypeId);
  }, [propertySecondaryTypeId]);

  function handleSecondaryPropertyTypeChange(value) {
    setSelectedPropertySecondaryType(value);
  }

  return {
    secondaryPropertyTypes,
    secondaryPropertyType: selectedPropertySecondaryType,
    handleSecondaryPropertyTypeChange,
  };
};

const useFetchOrgSecondaryPropertyTypes = (organizationId) => {
  const queryKey = 'secondaryPropertyTyes';
  const options = {
    initialData: [],
  };
  const { data } = useQuery(
    queryKey,
    () => {
      const propertyService = new PropertyService();
      return propertyService.getOrgSecondaryPropertyTypes(organizationId);
    },
    options,
  );

  return data;
};

// A contract row that is deep-equal to the one originally loaded has not been
// touched by the user this session. Some properties carry legacy HUD contract
// data that predates a later validation rule (e.g. the 12-character
// SD9900005002 on Meyer / Village Square, which the 11-character limit now
// rejects), and the user often cannot even correct it in place. Forgiving
// untouched rows keeps a stale legacy value from permanently disabling Save for
// the whole Property Info page, while any row the user adds or edits is still
// fully validated. Shared by every validator that walks the contract arrays so
// the exemption stays consistent across them.
export const isContractRowUnchanged = (contract, originalContracts = []) =>
  originalContracts.some(
    (originalContract) =>
      // Match the persisted row by id first (a cheap guard that also skips
      // freshly-added draft rows with no id), then confirm nothing changed.
      originalContract?.id != null &&
      originalContract.id === contract?.id &&
      equals(originalContract, contract),
  );

const hasContractNumber = (value) =>
  value != null && String(value).trim() !== '';

// Editing a saved contract number updates the row in place, so every unit on it follows. Adding one
// leaves those units where they were, and they have to be reassigned by hand, which is what the
// Property Info save warns about. A row counts as an addition when it has no persisted id, or when
// the row it came from had no contract number of its own yet.
const hasAddedContractNumber = (contracts, originalContracts = []) =>
  (contracts ?? []).some((contract) => {
    if (!hasContractNumber(contract?.contractNumber)) {
      return false;
    }
    if (contract?.id == null) {
      return true;
    }
    const originalContract = (originalContracts ?? []).find(
      ({ id } = {}) => id != null && id === contract.id,
    );
    return !hasContractNumber(originalContract?.contractNumber);
  });

export const hasNewHUDContractNumber = (setup, originalSetup) =>
  hasAddedContractNumber(
    setup?.primaryContracts,
    originalSetup?.primaryContracts,
  ) ||
  hasAddedContractNumber(
    setup?.secondaryContracts,
    originalSetup?.secondaryContracts,
  ) ||
  // With `passbookRate` off, Affordable Setup renders the old form, which edits
  // `setup.HUDContractNumber` directly instead of the contract rows, and the save turns that field
  // into a contract all the same. The new form mirrors the first contract onto it, so a
  // blank-to-filled move there is always an addition either way.
  (hasContractNumber(setup?.HUDContractNumber) &&
    !hasContractNumber(originalSetup?.HUDContractNumber));

export const useIsFormInvalid = ({
  propertyData,
  originalPropertyDetails,
  marketingData,
  affordableDetails,
  passbookRate,
  riskMgmtAssetProtectAdmin,
  interestRatesAreValid,
}: Record<string, Object>) => {
  const { affordableWaitlistV2, hudTracsIntegration } = useFlags();
  const hasLIHTCProgram = useMemo(() => {
    const affordablePrograms = affordableDetails?.programs ?? [];
    const programNames = affordablePrograms.map(
      (pap) => pap?.masterAffordableProgram?.name,
    );
    return programNames.includes('LIHTC');
  }, [affordableDetails?.programs]);

  const hasPHProgram = useMemo(() => {
    const affordablePrograms = affordableDetails?.programs ?? [];
    const programNames = affordablePrograms.map(
      (pap) => pap?.masterAffordableProgram?.name,
    );
    return programNames.includes('PH');
  }, [affordableDetails?.programs]);

  const hasHUDProgram = useMemo(() => {
    const affordablePrograms = affordableDetails?.programs ?? [];
    const programNames = affordablePrograms.map(
      (pap) => pap?.masterAffordableProgram?.name,
    );
    return programNames.includes('HUD');
  }, [affordableDetails?.programs]);

  const basicPropertyValidations = useMemo(() => {
    if (!cleanData(propertyData, 'name')) return true;
    if (!cleanData(propertyData, 'email') || !validateEmail(propertyData.email))
      return true;
    if (!validatePhoneNumber(propertyData.phoneNumber)) return true;
    if (!validatePhoneNumber(propertyData.fax)) return true;
    if (!cleanData(propertyData, 'address1')) return true;
    if (!cleanData(propertyData, 'city')) return true;
    if (!validateZipCode(propertyData.zip)) return true;
    if (propertyData.turnMoveOutDays < 1 || propertyData.turnMoveOutDays === '')
      return true;
    return false;
  }, [propertyData]);

  const setupValidations = useMemo(() => {
    const isValidEligibilityUniverseCode = (value) =>
      value === ELIGIBILITY_UNIVERSE_CODE_ENUM.PRE_1981 ||
      value === ELIGIBILITY_UNIVERSE_CODE_ENUM.POST_1981;

    const isValidMinimumRent = (value) => {
      if (value === '' || value === null || value === undefined) return false;
      const numValue = Number(value);
      return (
        Number.isInteger(numValue) &&
        numValue >= HUD_MINIMUM_RENT_MIN &&
        numValue <= HUD_MINIMUM_RENT_MAX
      );
    };

    const hasInvalidHUDContractRows = (
      contracts = [],
      originalContracts = [],
    ) => {
      return contracts.some((contract) => {
        const contractNumber = (contract?.contractNumber || '').trim();
        const eligibilityUniverseCode = contract?.eligibilityUniverseCode;
        const hasAnyContractData =
          contractNumber ||
          eligibilityUniverseCode ||
          contract?.effectiveDate ||
          contract?.expirationDate ||
          contract?.minimumRent === 0 ||
          !!contract?.minimumRent ||
          !!contract?.ownerDUNSNumber ||
          !!contract?.parentCompanyDUNSNumber ||
          !!contract?.ownerTIN ||
          !!contract?.parentCompanyTIN;

        // A blank unsaved row is an in-progress "add contract". A blank SAVED row is a removal in
        // disguise: dropped from the payload, the backend reads the missing id as a removal and
        // unassigns the units, with no confirmation. Invalid, so removal goes via the trash control.
        if (!hasAnyContractData) return Boolean(contract?.id);

        // Untouched legacy rows are left as-is; only edited or new rows are validated.
        if (isContractRowUnchanged(contract, originalContracts)) return false;

        if (hasHUDContractIdentifierErrors(contract)) return true;

        if (!contractNumber || contractNumber.length > 11) return true;
        if (
          eligibilityUniverseCode &&
          !isValidEligibilityUniverseCode(eligibilityUniverseCode)
        )
          return true;

        if (!isValidMinimumRent(contract?.minimumRent)) return true;

        return false;
      });
    };

    if (
      propertyData.setup &&
      (hasLIHTCProgram || !passbookRate) &&
      (!cleanData(propertyData.setup, 'stateId') ||
        !cleanData(propertyData.setup, 'projectIdTic'))
    ) {
      return true;
    }

    if (
      hasHUDProgram &&
      hasEditedHUDSetupIdentifierError(
        propertyData?.setup,
        originalPropertyDetails?.setup,
        hudTracsIntegration,
      )
    ) {
      return true;
    }

    if (hasPHProgram) {
      const hasEmptyField = ['agencyName', 'phaCode'].some((field) =>
        isEmpty(propertyData?.setup?.[field] ?? ''),
      );

      const isPHACodeValid = !isEmpty(propertyData?.setup?.phaCode ?? '')
        ? (propertyData?.setup?.phaCode ?? '').length === 5
        : true;

      return hasEmptyField || !isPHACodeValid;
    }

    // Whitespace-only is invalid, but a valid value must not short-circuit: these were early
    // returns, so a filled-in value skipped every check below, HUD contracts included.
    const isWhitespaceOnly = (value) =>
      typeof value === 'string' &&
      value.length > 0 &&
      value.trim().length === 0;

    if (
      isWhitespaceOnly(propertyData?.setup?.XMLPropertyName) ||
      isWhitespaceOnly(propertyData?.setup?.XMLProjectName)
    ) {
      return true;
    }

    const { loanTransferAmount, noteRentPayment, icPayment } =
      propertyData.setup || {};

    if (
      (loanTransferAmount != null && Number(loanTransferAmount) < 0) ||
      (noteRentPayment != null && Number(noteRentPayment) < 0) ||
      (icPayment != null && Number(icPayment) < 0)
    ) {
      return true;
    }

    const hudRepaymentCollectionFee =
      propertyData?.setup?.HUDRepaymentCollectionFeePercentage;
    if (
      hudRepaymentCollectionFee != null &&
      (Number(hudRepaymentCollectionFee) < 0 ||
        Number(hudRepaymentCollectionFee) > 20)
    ) {
      return true;
    }

    if (
      hasInvalidHUDContractRows(
        propertyData?.setup?.primaryContracts,
        originalPropertyDetails?.setup?.primaryContracts,
      ) ||
      hasInvalidHUDContractRows(
        propertyData?.setup?.secondaryContracts,
        originalPropertyDetails?.setup?.secondaryContracts,
      )
    ) {
      return true;
    }

    return false;
  }, [
    propertyData.setup,
    originalPropertyDetails?.setup,
    hasLIHTCProgram,
    passbookRate,
    hasPHProgram,
    hasHUDProgram,
    hudTracsIntegration,
  ]);

  const integrationValidations = useMemo(() => {
    if (
      propertyData.isDomusoActive &&
      (!numberInRange(propertyData.domusoPropertyCode.length, 4, 5) ||
        !cleanData(propertyData, 'domusoPropertyCode'))
    ) {
      return true;
    }

    return false;
  }, [propertyData]);

  const commercialValidations = useMemo(() => {
    if (
      propertyData.hasCommercialFloorPlans !== 'ALL' &&
      !cleanData(propertyData, 'applicationIncomeMultiplier')
    ) {
      return true;
    }
    return false;
  }, [propertyData]);

  const marketingValidations = useMemo(() => {
    if (!cleanData(marketingData, 'pitch1')) return true;

    if (
      cleanData(marketingData, 'petPolicyDescription') &&
      !validateMaxLength(marketingData.petPolicyDescription, 501)
    ) {
      return true;
    }

    if (
      cleanData(marketingData, 'parkingDetailDescription') &&
      !validateMaxLength(marketingData.parkingDetailDescription, 501)
    ) {
      return true;
    }

    return false;
  }, [marketingData]);

  const settingsValidations = useMemo(() => {
    if (
      propertyData.propertyCollectionSetting?.enabled &&
      (propertyData.propertyCollectionSetting.minimumAmount < 100 ||
        propertyData.propertyCollectionSetting.minimumAmount > 999)
    ) {
      return true;
    }

    if (
      propertyData.propertyCollectionSetting?.daysAfterFAS <= 0 ||
      propertyData.propertyCollectionSetting?.daysAfterFAS > 999
    ) {
      return true;
    }

    if (
      riskMgmtAssetProtectAdmin &&
      propertyData.insuranceType === ASSET_PROTECT &&
      propertyData.insuranceFeeAmount < 10
    ) {
      return true;
    }

    if (
      propertyData.config?.accrueSecurityDepositInterest &&
      !propertyData.config?.securityDepositInterestPayoutFrequency
    ) {
      return true;
    }

    return false;
  }, [
    propertyData.propertyCollectionSetting,
    propertyData.config,
    propertyData.insuranceType,
    propertyData.insuranceFeeAmount,
    riskMgmtAssetProtectAdmin,
  ]);

  const LAND_PARCEL_NUMBER_MAX_LENGTH = 50;
  const landParcelValidations = useMemo(() => {
    if (propertyData.config?.landParcelNumber) {
      if (
        propertyData.config?.landParcelNumber?.length >
        LAND_PARCEL_NUMBER_MAX_LENGTH
      ) {
        return true;
      }
    }
    return false;
  }, [propertyData.config?.landParcelNumber]);

  const stateConfigValidations = useMemo(() => {
    return propertyData.stateConfigs?.some(
      (stateConfig) =>
        stateConfig.value &&
        stateConfig.key === 'CDLACNumber' &&
        !CDLAC_NUMBER_VALIDATION_REGEX.test(stateConfig.value),
    );
  }, [propertyData.stateConfigs]);

  const dateValidations = useMemo(() => {
    const setup = propertyData?.setup;
    if (!setup) return false;

    const hasArrayDateOrderIssue = (contracts = [], originalContracts = []) => {
      return contracts.some((contract) => {
        if (!contract?.effectiveDate || !contract?.expirationDate) return false;
        // Untouched legacy rows are left as-is; only edited or new rows are validated.
        if (isContractRowUnchanged(contract, originalContracts)) return false;
        return moment(contract.expirationDate).isBefore(
          moment(contract.effectiveDate),
        );
      });
    };

    if (
      hasArrayDateOrderIssue(
        setup.primaryContracts,
        originalPropertyDetails?.setup?.primaryContracts,
      )
    ) {
      return true;
    }

    if (
      hasArrayDateOrderIssue(
        setup.secondaryContracts,
        originalPropertyDetails?.setup?.secondaryContracts,
      )
    ) {
      return true;
    }

    // HUD Primary: Check if expiration is before effective date
    if (setup.HUDContractEffectiveDate && setup.HUDContractExpirationDate) {
      if (
        moment(setup.HUDContractExpirationDate).isBefore(
          moment(setup.HUDContractEffectiveDate),
        )
      ) {
        return true;
      }
    }

    // HUD Secondary: Check if expiration is before effective date
    if (
      setup.HUDSecondaryContractEffectiveDate &&
      setup.HUDSecondaryContractExpirationDate
    ) {
      if (
        moment(setup.HUDSecondaryContractExpirationDate).isBefore(
          moment(setup.HUDSecondaryContractEffectiveDate),
        )
      ) {
        return true;
      }
    }

    // LIHTC: Check if expiration is before effective date
    if (setup.LIHTCContractEffectiveDate && setup.LIHTCContractExpirationDate) {
      if (
        moment(setup.LIHTCContractExpirationDate).isBefore(
          moment(setup.LIHTCContractEffectiveDate),
        )
      ) {
        return true;
      }
    }

    // RD: Check if expiration is before effective date
    if (setup.RDContractEffectiveDate && setup.RDContractExpirationDate) {
      if (
        moment(setup.RDContractExpirationDate).isBefore(
          moment(setup.RDContractEffectiveDate),
        )
      ) {
        return true;
      }
    }

    // WaitlistV2: close date failed inline validation (typed an invalid or
    // out-of-range date). Unlike the open date this cannot be derived — the
    // rejected input never reaches form state — so it stays a flag written by
    // WaitlistV2/hooks.js and excluded from the API payload in
    // ViewProperty/index.js. Scoped to an enabled waitlist so a hidden section
    // cannot gate the page.
    if (setup.affordableWaitlist && setup.waitlistV2?.closeDateError)
      return true;

    // ENG-32125: an open waitlist needs an open date. Safe to require on stored
    // state as well as edited state because the Open Date field renders whenever
    // the waitlist is enabled, so there is always a control to satisfy it — which
    // is not true once the waitlist is switched off, hence the `enabled` term.
    // Gated on the flag because the legacy waitlist UI writes the same
    // affordableWaitlistStatus and has no open-date control at all.
    // Delete this condition with the rest of the affordable-waitlist-v2 cleanup.
    if (
      affordableWaitlistV2 &&
      isOpenDateMissing(
        setup.affordableWaitlist,
        setup.affordableWaitlistStatus === 'OPEN',
        setup.waitlistV2?.scheduledOpenDate,
      )
    ) {
      return true;
    }

    return false;
  }, [
    propertyData?.setup,
    originalPropertyDetails?.setup,
    affordableWaitlistV2,
  ]);

  return useMemo(() => {
    return (
      basicPropertyValidations ||
      setupValidations ||
      landParcelValidations ||
      integrationValidations ||
      commercialValidations ||
      marketingValidations ||
      settingsValidations ||
      stateConfigValidations ||
      dateValidations ||
      !interestRatesAreValid
    );
  }, [
    basicPropertyValidations,
    setupValidations,
    integrationValidations,
    commercialValidations,
    marketingValidations,
    settingsValidations,
    interestRatesAreValid,
    landParcelValidations,
    stateConfigValidations,
    dateValidations,
  ]);
};
